import { useCallback, useSyncExternalStore } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

type AlertType = "success" | "error" | "warning" | "info";

interface AlertState {
  open: boolean;
  type: AlertType;
  message: string;
  /**
   * '확인' 클릭 후 호출되는 콜백.
   * - 닫힘 처리가 끝난 뒤 호출되므로 callback 내부에서 focus()/scrollIntoView() 등
   *   포커스 이동을 안전하게 수행할 수 있다.
   * - 한 번 호출되면 다음 alert 에 새는 것을 막기 위해 close 단계에서 초기화한다.
   */
  onConfirm?: () => void;
}

const TITLE_MAP: Record<AlertType, string> = {
  success: "알림",
  error: "오류",
  warning: "경고",
  info: "알림",
};

let state: AlertState = { open: false, type: "info", message: "" };
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

function show(type: AlertType, message: string, onConfirm?: () => void) {
  state = { open: true, type, message, onConfirm };
  emit();
}

function close() {
  // 닫힘 emit 후 React 가 modal 을 unmount 한 다음에 콜백을 실행하도록
  // 마이크로태스크 한 단계 지연시켜 focus 이동이 dialog 잔여 포커스에 묻히지 않게 한다.
  const cb = state.onConfirm;
  state = { ...state, open: false, onConfirm: undefined };
  emit();
  if (cb) {
    queueMicrotask(cb);
  }
}

export const alertModal = {
  success: (msg: string, onConfirm?: () => void) =>
    show("success", msg, onConfirm),
  error: (msg: string, onConfirm?: () => void) => show("error", msg, onConfirm),
  warning: (msg: string, onConfirm?: () => void) =>
    show("warning", msg, onConfirm),
  info: (msg: string, onConfirm?: () => void) => show("info", msg, onConfirm),
};

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return state;
}

export function AlertModal() {
  const current = useSyncExternalStore(subscribe, getSnapshot);

  const handleClose = useCallback(() => {
    close();
  }, []);

  return (
    <AlertDialog open={current.open} onOpenChange={(v) => !v && handleClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{TITLE_MAP[current.type]}</AlertDialogTitle>
          <AlertDialogDescription>{current.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={handleClose}>확인</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
